import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Loader2, UserPlus, ClipboardPaste, Trash2, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreatePerson, useUpdatePerson } from '@/api';
import type { Person } from '@/types/leads';
import type { AxiosError } from 'axios';
import styles from './AddLeadDrawer.module.css';

interface ApiFieldErrors {
  firstName?: string[];
  lastName?: string[];
  email?: string[];
  phoneNumber?: string[];
  non_field_errors?: string[];
  [key: string]: string[] | string | undefined;
}

type Mode = 'single' | 'paste';

type RowStatus = 'ready' | 'invalid' | 'pending' | 'added' | 'merged' | 'duplicate' | 'error';

interface ParsedRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  status: RowStatus;
  errorMessage?: string;
}

interface AddLeadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (person: Person, existing: boolean) => void;
  onBulkSuccess?: (persons: Person[]) => void;
  channel: 'EMAIL' | 'WHATSAPP';
  initialEmail?: string;
  initialPhone?: string;
  /** When set, the drawer opens in edit mode: single-tab, pre-filled, PATCH on submit */
  editingPerson?: Person | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-().]{7,}$/;

// Paste rows are submitted sequentially so backend dedup sees the first
// insert committed before the second lookup runs. Parallelizing here caused
// duplicates when the same contact appeared twice in the same paste.
const CONCURRENCY = 1;

function normalizePhoneForDedup(raw: string): string {
  return raw.replace(/[^\d+]/g, '');
}

function detectSeparator(line: string): string {
  if (line.includes('\t')) return '\t';
  if (line.includes(';')) return ';';
  if (line.includes(',')) return ',';
  return '\t';
}

function isEmailLike(value: string): boolean {
  return value.includes('@') && EMAIL_REGEX.test(value.trim());
}

function isPhoneLike(value: string): boolean {
  const cleaned = value.replace(/[\s\-().]/g, '');
  return /^\+?\d{7,16}$/.test(cleaned);
}

type ColumnType = 'firstName' | 'lastName' | 'name' | 'email' | 'phoneNumber' | 'unknown';

// Whitelist of exact header labels (normalized lowercase). Anything else returns
// 'unknown' so a data cell like "juliasotelocarlos@gmail.com" can't be mistaken
// for an "email" header just because the substring "mail" appears inside it.
const HEADER_TERMS: Record<string, ColumnType> = {
  email: 'email',
  'e-mail': 'email',
  emails: 'email',
  mail: 'email',
  correo: 'email',
  'correo electronico': 'email',
  'correo electrónico': 'email',

  phone: 'phoneNumber',
  'phone number': 'phoneNumber',
  phones: 'phoneNumber',
  telefono: 'phoneNumber',
  teléfono: 'phoneNumber',
  telefonos: 'phoneNumber',
  teléfonos: 'phoneNumber',
  tel: 'phoneNumber',
  telf: 'phoneNumber',
  whatsapp: 'phoneNumber',
  wa: 'phoneNumber',
  celular: 'phoneNumber',
  cel: 'phoneNumber',
  movil: 'phoneNumber',
  móvil: 'phoneNumber',
  mobile: 'phoneNumber',

  'first name': 'firstName',
  firstname: 'firstName',
  first: 'firstName',
  nombre: 'firstName',
  nombres: 'firstName',

  'last name': 'lastName',
  lastname: 'lastName',
  last: 'lastName',
  apellido: 'lastName',
  apellidos: 'lastName',
  surname: 'lastName',

  name: 'name',
  'full name': 'name',
  fullname: 'name',
  'nombre completo': 'name',
  contact: 'name',
  contacto: 'name',
};

function normalizeHeader(h: string): ColumnType {
  const s = h.toLowerCase().trim();
  if (!s) return 'unknown';
  // Reject anything that looks like a value, never a header
  if (s.includes('@')) return 'unknown';
  if (/^\+?\d/.test(s.replace(/[\s\-().]/g, ''))) return 'unknown';
  if (s.length > 30) return 'unknown';
  return HEADER_TERMS[s] || 'unknown';
}

function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function validateRow(row: ParsedRow): { status: RowStatus; errorMessage?: string } {
  const hasEmail = row.email.trim().length > 0;
  const hasPhone = row.phoneNumber.trim().length > 0;
  const hasName = row.firstName.trim().length > 0 || row.lastName.trim().length > 0;

  if (!hasEmail && !hasPhone && !hasName) {
    return { status: 'invalid', errorMessage: 'Enter name, email, or phone' };
  }
  if (hasEmail && !EMAIL_REGEX.test(row.email.trim())) {
    return { status: 'invalid', errorMessage: 'Invalid email format' };
  }
  if (hasPhone && !PHONE_REGEX.test(row.phoneNumber.trim())) {
    return { status: 'invalid', errorMessage: 'Invalid phone format' };
  }
  return { status: 'ready' };
}

/** Guess the column type from one data cell */
function guessColumnFromCell(cell: string): ColumnType {
  const v = cell.trim();
  if (!v) return 'unknown';
  if (isEmailLike(v)) return 'email';
  if (isPhoneLike(v)) return 'phoneNumber';
  return 'name';
}

/** Build a column map across ALL data rows, not just the first.
 *
 *  Each column position gets its "majority" type across non-empty cells. This
 *  fixes the case where the first row is missing phone (`Name\t\tEmail`) but
 *  most rows have it (`Name\tPhone\tEmail`): previously column 1 was locked
 *  to 'name' from the first row's empty cell, then all the phone values on
 *  subsequent rows clobbered firstName. Now column 1 is detected as 'phoneNumber'
 *  from the other rows and the first row's empty phone just stays empty.
 */
function buildColumnMapFromDataRows(rows: string[][]): ColumnType[] {
  const maxCols = Math.max(...rows.map((r) => r.length));
  const result: ColumnType[] = [];
  for (let col = 0; col < maxCols; col++) {
    const counts: Record<ColumnType, number> = {
      email: 0,
      phoneNumber: 0,
      name: 0,
      firstName: 0,
      lastName: 0,
      unknown: 0,
    };
    for (const row of rows) {
      const cell = row[col];
      if (cell === undefined) continue;
      counts[guessColumnFromCell(cell)] += 1;
    }
    // Pick the type with the most non-unknown votes, fall back to 'name'
    const sorted = (Object.entries(counts) as [ColumnType, number][]).filter(
      ([k]) => k !== 'unknown',
    );
    sorted.sort((a, b) => b[1] - a[1]);
    result.push(sorted[0] && sorted[0][1] > 0 ? sorted[0][0] : 'unknown');
  }
  return result;
}

function parsePasteText(text: string): ParsedRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const separator = detectSeparator(lines[0]);
  const firstCells = lines[0].split(separator).map((c) => c.trim());

  // Detect header row (strict whitelist match so a data cell containing
  // "gmail.com" doesn't get mistaken for an email header).
  const headerMap = firstCells.map(normalizeHeader);
  const hasHeader = headerMap.some((h) => h !== 'unknown');

  let columnMap: ColumnType[];
  let dataLines: string[];

  if (hasHeader) {
    columnMap = headerMap;
    dataLines = lines.slice(1);
  } else {
    dataLines = lines;
    // Parse every row and build a majority column map so varying column fills
    // (some rows have phone, some don't) still map correctly.
    const parsedRows = dataLines.map((l) => l.split(separator).map((c) => c.trim()));
    columnMap = buildColumnMapFromDataRows(parsedRows);
  }

  return dataLines.map((line, idx) => {
    const cells = line.split(separator).map((c) => c.trim());
    let firstName = '';
    let lastName = '';
    let email = '';
    let phoneNumber = '';

    cells.forEach((cell, i) => {
      if (!cell) return;
      const col = columnMap[i] || 'unknown';
      // Guard: if the column was inferred but this cell clearly doesn't match
      // (e.g. 'phoneNumber' column but the cell contains '@'), fall through
      // to content detection instead of silently putting the wrong value in.
      let effectiveCol: ColumnType = col;
      if (col === 'email' && !isEmailLike(cell)) effectiveCol = 'unknown';
      else if (col === 'phoneNumber' && !isPhoneLike(cell)) effectiveCol = 'unknown';

      if (effectiveCol === 'email') email = cell;
      else if (effectiveCol === 'phoneNumber') phoneNumber = cell;
      else if (effectiveCol === 'firstName') firstName = cell;
      else if (effectiveCol === 'lastName') lastName = cell;
      else if (effectiveCol === 'name') {
        const split = splitName(cell);
        if (!firstName) firstName = split.firstName;
        if (!lastName) lastName = split.lastName;
      } else {
        // Unknown — infer from content
        if (!email && isEmailLike(cell)) email = cell;
        else if (!phoneNumber && isPhoneLike(cell)) phoneNumber = cell;
        else if (!firstName && !lastName) {
          const split = splitName(cell);
          firstName = split.firstName;
          lastName = split.lastName;
        }
      }
    });

    const row: ParsedRow = {
      id: `row-${idx}-${Date.now()}`,
      firstName,
      lastName,
      email,
      phoneNumber,
      status: 'ready',
    };
    const { status, errorMessage } = validateRow(row);
    row.status = status;
    row.errorMessage = errorMessage;
    return row;
  });
}

export function AddLeadDrawer({
  isOpen,
  onClose,
  onSuccess,
  onBulkSuccess,
  channel,
  initialEmail = '',
  initialPhone = '',
  editingPerson = null,
}: AddLeadDrawerProps) {
  const isEditMode = Boolean(editingPerson);
  const [mode, setMode] = useState<Mode>('single');

  // Single mode state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [fieldErrors, setFieldErrors] = useState<ApiFieldErrors>({});

  // Paste mode state
  const [pasteText, setPasteText] = useState('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const firstNameRef = useRef<HTMLInputElement>(null);
  const pasteAreaRef = useRef<HTMLTextAreaElement>(null);
  const { mutateAsync: createPerson, isPending: isCreating } = useCreatePerson();
  const { mutateAsync: updatePerson, isPending: isUpdating } = useUpdatePerson();
  const isPending = isCreating || isUpdating;

  // When editing, force single mode and prefill from the person record
  useEffect(() => {
    if (isOpen && editingPerson) {
      setMode('single');
      // Prefer the split name fields. Some callers (e.g. the campaign recipient
      // lists) only carry `fullName`, so fall back to splitting it on the first
      // space — otherwise a save would blank the contact's name.
      let first = editingPerson.firstName || '';
      let last = editingPerson.lastName || '';
      if (!first && !last && editingPerson.fullName) {
        const parts = editingPerson.fullName.trim().split(/\s+/);
        first = parts.shift() || '';
        last = parts.join(' ');
      }
      setFirstName(first);
      setLastName(last);
      setEmail(editingPerson.email || '');
      setPhoneNumber(editingPerson.phoneNumber || '');
      setFieldErrors({});
    }
  }, [isOpen, editingPerson]);

  // Sync initial values when they change (drawer opened with prefill for create)
  useEffect(() => {
    if (editingPerson) return;
    setEmail(initialEmail);
    setPhoneNumber(initialPhone);
  }, [initialEmail, initialPhone, editingPerson]);

  // Focus first field on open
  useEffect(() => {
    if (isOpen && mode === 'single') {
      const t = setTimeout(() => firstNameRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    if (isOpen && mode === 'paste') {
      const t = setTimeout(() => pasteAreaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen, mode]);

  // Clear state when drawer opens fresh
  useEffect(() => {
    if (isOpen) {
      setFieldErrors({});
    } else {
      // Reset paste state when closed so next open starts clean
      setPasteText('');
      setParsedRows([]);
      setBulkProgress({ done: 0, total: 0 });
      setIsBulkSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isPending || isBulkSubmitting) return;
    onClose();
  }, [isPending, isBulkSubmitting, onClose]);

  // Keyboard: Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  // Re-parse when text changes (while not submitting)
  useEffect(() => {
    if (mode !== 'paste' || isBulkSubmitting) return;
    if (!pasteText.trim()) {
      setParsedRows([]);
      return;
    }
    const rows = parsePasteText(pasteText);
    setParsedRows(rows);
  }, [pasteText, mode, isBulkSubmitting]);

  const handleRowEdit = (id: string, field: keyof ParsedRow, value: string) => {
    setParsedRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        const { status, errorMessage } = validateRow(updated);
        return { ...updated, status, errorMessage };
      }),
    );
  };

  const handleRowRemove = (id: string) => {
    setParsedRows((prev) => prev.filter((r) => r.id !== id));
  };

  const readyRows = useMemo(
    () => parsedRows.filter((r) => r.status === 'ready' || r.status === 'error'),
    [parsedRows],
  );
  // Duplicate rows in the same paste — flag them as invalid so the submit
  // button excludes them and the backend doesn't have to race against itself.
  useEffect(() => {
    if (mode !== 'paste' || isBulkSubmitting) return;
    setParsedRows((prev) => {
      const seenEmails = new Set<string>();
      const seenPhones = new Set<string>();
      let changed = false;
      const next = prev.map((r) => {
        if (r.status !== 'ready' && r.status !== 'invalid') return r;
        const key = r.email.trim().toLowerCase();
        const phoneKey = normalizePhoneForDedup(r.phoneNumber.trim());
        const dupEmail = key && seenEmails.has(key);
        const dupPhone = phoneKey && seenPhones.has(phoneKey);
        if (dupEmail || dupPhone) {
          if (r.status !== 'invalid' || r.errorMessage !== 'Duplicate row in paste') {
            changed = true;
            return { ...r, status: 'invalid' as const, errorMessage: 'Duplicate row in paste' };
          }
          return r;
        }
        if (key) seenEmails.add(key);
        if (phoneKey) seenPhones.add(phoneKey);
        // A row previously flagged as duplicate should re-validate if the user edited it
        if (r.status === 'invalid' && r.errorMessage === 'Duplicate row in paste') {
          const v = validateRow(r);
          changed = true;
          return { ...r, status: v.status, errorMessage: v.errorMessage };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [parsedRows, mode, isBulkSubmitting]);
  const invalidCount = useMemo(
    () => parsedRows.filter((r) => r.status === 'invalid').length,
    [parsedRows],
  );

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    // Edit mode: PATCH the existing person
    if (editingPerson) {
      try {
        const updated = await updatePerson({
          id: editingPerson.id,
          payload: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim() || undefined,
            phoneNumber: phoneNumber.trim() || undefined,
          },
        });
        const label =
          [firstName, lastName].filter(Boolean).join(' ') || updated.fullName || 'contact';
        toast.success(`Updated ${label}`, { duration: 3000 });
        onClose();
        onSuccess(updated, true);
        return;
      } catch (err) {
        const axiosErr = err as AxiosError<ApiFieldErrors>;
        const status = axiosErr.response?.status;
        const data = axiosErr.response?.data;
        if (status === 400 && data) {
          setFieldErrors(data);
        } else if (status === 403) {
          toast.error(
            'This contact was imported from scraping — editing is only allowed for contacts you added manually.',
            { duration: 8000 },
          );
        } else if (status === 404) {
          toast.error('Contact not found. It may have been deleted.', { duration: 8000 });
        } else {
          toast.error('Could not update contact. Please try again.', { duration: 8000 });
        }
        return;
      }
    }

    try {
      const result = await createPerson({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        channel,
      });

      const name =
        [firstName, lastName].filter(Boolean).join(' ') ||
        result.person.fullName ||
        email ||
        phoneNumber ||
        'contact';
      if (result.existing && result.merged) {
        toast.success(`Updated ${name} — ready to send`, { duration: 3000 });
      } else if (result.existing) {
        toast(`${name} is already in your list — reusing it`, {
          icon: '🔵',
          duration: 4000,
          style: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
        });
      } else {
        toast.success(`Added ${name} — ready to send`, { duration: 3000 });
      }

      setFirstName('');
      setLastName('');
      setEmail('');
      setPhoneNumber('');
      onClose();
      onSuccess(result.person, result.existing);
    } catch (err) {
      const axiosErr = err as AxiosError<ApiFieldErrors>;
      const status = axiosErr.response?.status;
      const data = axiosErr.response?.data;

      if (status === 400 && data) {
        if (
          data.email?.some(
            (msg) =>
              msg.toLowerCase().includes('suppressed') ||
              msg.toLowerCase().includes('bounced') ||
              msg.toLowerCase().includes('opted'),
          )
        ) {
          setFieldErrors({
            email: ["This email opted out or bounced before. We won't send here."],
          });
        } else {
          setFieldErrors(data);
        }
      } else if (status === 401) {
        toast.error('Session expired. Please log in again.', { duration: 8000 });
      } else if (status === 403) {
        toast.error("You don't have permission to add contacts.", { duration: 8000 });
      } else {
        toast.error('Something went wrong. Please try again.', { duration: 8000 });
      }
    }
  };

  const handleBulkSubmit = async () => {
    if (readyRows.length === 0 || isBulkSubmitting) return;

    setIsBulkSubmitting(true);
    setBulkProgress({ done: 0, total: readyRows.length });

    // Mark all ready rows as pending
    setParsedRows((prev) =>
      prev.map((r) =>
        r.status === 'ready' || r.status === 'error'
          ? { ...r, status: 'pending', errorMessage: undefined }
          : r,
      ),
    );

    const addedPersons: Person[] = [];
    let newCount = 0;
    let mergedCount = 0;
    let duplicateCount = 0;
    const queue = [...readyRows];
    let done = 0;

    const worker = async () => {
      while (queue.length > 0) {
        const row = queue.shift();
        if (!row) break;
        try {
          const result = await createPerson({
            firstName: row.firstName.trim(),
            lastName: row.lastName.trim(),
            email: row.email.trim() || undefined,
            phoneNumber: row.phoneNumber.trim() || undefined,
            channel,
          });
          addedPersons.push(result.person);
          let nextStatus: RowStatus;
          if (!result.existing) {
            nextStatus = 'added';
            newCount += 1;
          } else if (result.merged) {
            nextStatus = 'merged';
            mergedCount += 1;
          } else {
            nextStatus = 'duplicate';
            duplicateCount += 1;
          }
          setParsedRows((prev) =>
            prev.map((r) =>
              r.id === row.id ? { ...r, status: nextStatus, errorMessage: undefined } : r,
            ),
          );
        } catch (err) {
          const axiosErr = err as AxiosError<ApiFieldErrors>;
          const data = axiosErr.response?.data;
          let message = 'Failed';
          if (data) {
            const firstField = (
              ['email', 'phoneNumber', 'firstName', 'lastName', 'non_field_errors'] as const
            ).find((k) => data[k]?.[0]);
            if (firstField && data[firstField]?.[0]) message = data[firstField]![0];
          }
          setParsedRows((prev) =>
            prev.map((r) =>
              r.id === row.id ? { ...r, status: 'error', errorMessage: message } : r,
            ),
          );
        } finally {
          done += 1;
          setBulkProgress({ done, total: readyRows.length });
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, readyRows.length) }, () => worker());
    await Promise.all(workers);

    setIsBulkSubmitting(false);

    const successCount = addedPersons.length;
    const errorCount = readyRows.length - successCount;
    const summaryBits: string[] = [];
    if (newCount > 0) summaryBits.push(`${newCount} added`);
    if (mergedCount > 0) summaryBits.push(`${mergedCount} updated`);
    if (duplicateCount > 0) summaryBits.push(`${duplicateCount} reused`);

    if (successCount > 0 && errorCount === 0) {
      toast.success(summaryBits.join(', ') + ' — ready to send', { duration: 3500 });
    } else if (successCount > 0 && errorCount > 0) {
      toast(`${summaryBits.join(', ')}, ${errorCount} failed — review the list`, {
        icon: '⚠️',
        duration: 6000,
        style: { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' },
      });
    } else {
      toast.error(`No contacts were added. Review the list and try again.`, { duration: 8000 });
    }

    if (addedPersons.length > 0) {
      if (onBulkSuccess) {
        onBulkSuccess(addedPersons);
      } else {
        addedPersons.forEach((p) => onSuccess(p, false));
      }
    }

    // Auto-close if everything succeeded
    if (errorCount === 0 && successCount > 0) {
      setTimeout(() => {
        onClose();
      }, 500);
    }
  };

  const getFieldError = (field: keyof ApiFieldErrors): string | undefined => {
    const errs = fieldErrors[field];
    if (!errs) return undefined;
    if (Array.isArray(errs)) return errs[0];
    return String(errs);
  };

  if (!isOpen) return null;

  const rowStatusLabel: Record<RowStatus, string> = {
    ready: 'Ready',
    invalid: 'Needs fix',
    pending: 'Adding...',
    added: 'Added',
    merged: 'Updated',
    duplicate: 'Reused',
    error: 'Failed',
  };

  return (
    <>
      <div className={styles.backdrop} onClick={handleClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit contact' : 'Quick add contact'}
        className={`${styles.drawer} ${mode === 'paste' && !isEditMode ? styles.drawerWide : ''}`}
      >
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerTitle}>{isEditMode ? 'Edit contact' : 'Add contacts'}</h2>
            <p className={styles.drawerSubtitle}>
              {isEditMode
                ? 'Fix the name, email, or phone for this contact.'
                : mode === 'single'
                  ? 'One contact at a time'
                  : 'Paste a list from Excel, Google Sheets, or CSV'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className={styles.closeBtn}
            aria-label="Close drawer"
            disabled={isPending || isBulkSubmitting}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>

        {!isEditMode && (
          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'single'}
              className={`${styles.tab} ${mode === 'single' ? styles.tabActive : ''}`}
              onClick={() => setMode('single')}
              disabled={isBulkSubmitting}
            >
              <UserPlus className={styles.tabIcon} />
              Single
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'paste'}
              className={`${styles.tab} ${mode === 'paste' ? styles.tabActive : ''}`}
              onClick={() => setMode('paste')}
              disabled={isBulkSubmitting}
            >
              <ClipboardPaste className={styles.tabIcon} />
              Paste list
            </button>
          </div>
        )}

        {mode === 'single' || isEditMode ? (
          <form onSubmit={handleSingleSubmit} className={styles.form} noValidate>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="add-lead-first-name" className={styles.label}>
                  First name
                </label>
                <input
                  ref={firstNameRef}
                  id="add-lead-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={`${styles.input} ${getFieldError('firstName') ? styles.inputError : ''}`}
                  placeholder="Juan"
                  disabled={isPending}
                  autoComplete="given-name"
                />
                {getFieldError('firstName') && (
                  <span className={styles.fieldError}>{getFieldError('firstName')}</span>
                )}
              </div>

              <div className={styles.field}>
                <label htmlFor="add-lead-last-name" className={styles.label}>
                  Last name
                </label>
                <input
                  id="add-lead-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={`${styles.input} ${getFieldError('lastName') ? styles.inputError : ''}`}
                  placeholder="García"
                  disabled={isPending}
                  autoComplete="family-name"
                />
                {getFieldError('lastName') && (
                  <span className={styles.fieldError}>{getFieldError('lastName')}</span>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="add-lead-email" className={styles.label}>
                Email
              </label>
              <input
                id="add-lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${styles.input} ${getFieldError('email') ? styles.inputError : ''}`}
                placeholder="juan@ejemplo.com"
                disabled={isPending}
                autoComplete="email"
              />
              {getFieldError('email') && (
                <span className={styles.fieldError}>{getFieldError('email')}</span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="add-lead-phone" className={styles.label}>
                Phone number
              </label>
              <input
                id="add-lead-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className={`${styles.input} ${getFieldError('phoneNumber') ? styles.inputError : ''}`}
                placeholder="+51 999 123 456"
                disabled={isPending}
                autoComplete="tel"
              />
              {getFieldError('phoneNumber') && (
                <span className={styles.fieldError}>{getFieldError('phoneNumber')}</span>
              )}
            </div>

            <p className={styles.hint}>
              Enter at least a name, email, or phone. We'll merge if the contact already exists.
            </p>

            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleClose}
                className={styles.cancelBtn}
                disabled={isPending}
              >
                Cancel
              </button>
              <button type="submit" className={styles.submitBtn} disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className={styles.spinner} />
                    {isEditMode ? 'Saving...' : 'Adding...'}
                  </>
                ) : isEditMode ? (
                  'Save changes'
                ) : (
                  'Add contact'
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.pasteBody}>
            <div className={styles.pasteIntro}>
              <p className={styles.pasteHelp}>
                Copy rows from an Excel file or Google Sheet and paste them below. We'll detect the
                columns automatically.
              </p>
              <div className={styles.pasteHint}>
                <span className={styles.hintLabel}>Accepted columns:</span>
                <span>First name · Last name · Full name · Email · Phone</span>
              </div>
            </div>

            <textarea
              ref={pasteAreaRef}
              className={styles.pasteArea}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={
                channel === 'EMAIL'
                  ? 'Juan García\tjuan@example.com\nMaría López\tmaria@example.com'
                  : 'Juan García\t+51 999 111 222\nMaría López\t+51 888 333 444'
              }
              spellCheck={false}
              disabled={isBulkSubmitting}
              rows={6}
            />

            {parsedRows.length > 0 && (
              <>
                <div className={styles.previewHeader}>
                  <div className={styles.previewSummary}>
                    <span className={styles.summaryMain}>
                      {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'} detected
                    </span>
                    {invalidCount > 0 && (
                      <span className={styles.summaryWarn}>
                        <AlertCircle className={styles.summaryIcon} />
                        {invalidCount} need{invalidCount === 1 ? 's' : ''} fixing
                      </span>
                    )}
                  </div>
                  {isBulkSubmitting && (
                    <span className={styles.progress}>
                      {bulkProgress.done} / {bulkProgress.total}
                    </span>
                  )}
                </div>

                <div className={styles.previewTableWrap}>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th>First name</th>
                        <th>Last name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th className={styles.colStatus}>Status</th>
                        <th aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row) => (
                        <tr
                          key={row.id}
                          className={`${styles.previewRow} ${
                            row.status === 'invalid' || row.status === 'error'
                              ? styles.previewRowError
                              : ''
                          } ${row.status === 'added' ? styles.previewRowAdded : ''} ${
                            row.status === 'merged' || row.status === 'duplicate'
                              ? styles.previewRowDup
                              : ''
                          }`}
                        >
                          <td>
                            <input
                              type="text"
                              className={styles.cellInput}
                              value={row.firstName}
                              onChange={(e) => handleRowEdit(row.id, 'firstName', e.target.value)}
                              disabled={
                                isBulkSubmitting ||
                                row.status === 'added' ||
                                row.status === 'merged' ||
                                row.status === 'duplicate'
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className={styles.cellInput}
                              value={row.lastName}
                              onChange={(e) => handleRowEdit(row.id, 'lastName', e.target.value)}
                              disabled={
                                isBulkSubmitting ||
                                row.status === 'added' ||
                                row.status === 'merged' ||
                                row.status === 'duplicate'
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="email"
                              className={styles.cellInput}
                              value={row.email}
                              onChange={(e) => handleRowEdit(row.id, 'email', e.target.value)}
                              disabled={
                                isBulkSubmitting ||
                                row.status === 'added' ||
                                row.status === 'merged' ||
                                row.status === 'duplicate'
                              }
                              placeholder="—"
                            />
                          </td>
                          <td>
                            <input
                              type="tel"
                              className={styles.cellInput}
                              value={row.phoneNumber}
                              onChange={(e) => handleRowEdit(row.id, 'phoneNumber', e.target.value)}
                              disabled={
                                isBulkSubmitting ||
                                row.status === 'added' ||
                                row.status === 'merged' ||
                                row.status === 'duplicate'
                              }
                              placeholder="—"
                            />
                          </td>
                          <td className={styles.colStatus}>
                            <span
                              className={`${styles.statusPill} ${styles[`status_${row.status}`]}`}
                            >
                              {row.status === 'pending' && <Loader2 className={styles.pillIcon} />}
                              {row.status === 'added' && <Check className={styles.pillIcon} />}
                              {row.status === 'merged' && <Check className={styles.pillIcon} />}
                              {row.status === 'invalid' && (
                                <AlertCircle className={styles.pillIcon} />
                              )}
                              {row.status === 'error' && (
                                <AlertCircle className={styles.pillIcon} />
                              )}
                              {rowStatusLabel[row.status]}
                            </span>
                            {row.errorMessage && (
                              <span className={styles.rowError}>{row.errorMessage}</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={styles.removeBtn}
                              onClick={() => handleRowRemove(row.id)}
                              disabled={isBulkSubmitting}
                              aria-label="Remove row"
                            >
                              <Trash2 className={styles.removeIcon} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleClose}
                className={styles.cancelBtn}
                disabled={isBulkSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={handleBulkSubmit}
                disabled={isBulkSubmitting || readyRows.length === 0}
              >
                {isBulkSubmitting ? (
                  <>
                    <Loader2 className={styles.spinner} />
                    Adding {bulkProgress.done} of {bulkProgress.total}...
                  </>
                ) : readyRows.length === 0 ? (
                  'Paste contacts to continue'
                ) : (
                  `Add ${readyRows.length} contact${readyRows.length === 1 ? '' : 's'}`
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
